/**
 * DetailsTab Component
 * 
 * Match details form: opponent, date, kick-off time, venue, status,
 * score, cup settings, extra time, and penalties.
 */
import { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Mic, Loader2, Check, X as XIcon, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { useVoiceCapture } from '../../hooks/useVoiceCapture';
import { useAuth } from '../../context/AuthContext';
import { VENUES, STATUSES } from './constants';
import { LineupImportModal } from '../LineupImportModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MATCH_TYPE_LABELS = {
  league: 'League',
  cup_knockout: 'Cup (Knockout)',
  cup_group: 'Cup (Group Stage)',
  friendly: 'Friendly',
};

function ComboboxInput({ value, onChange, options, placeholder, testId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes((search || value || '').toLowerCase())
  );

  const handleSelect = (option) => {
    onChange(option);
    setSearch('');
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearch(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={search || value}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onBlur={() => { setTimeout(() => { if (!dropdownRef.current?.contains(document.activeElement)) { setIsOpen(false); setSearch(''); } }, 150); }}
        placeholder={placeholder}
        className="rounded-xl"
        data-testid={testId}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto"
        >
          {filteredOptions.map((option, idx) => (
            <button
              key={idx}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(option); }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CUP_ROUNDS = [
  { value: 'group_stage', label: 'Group Stage' },
  { value: 'preliminary', label: 'Preliminary Round' },
  { value: 'round_1', label: '1st Round' },
  { value: 'round_2', label: '2nd Round' },
  { value: 'round_3', label: '3rd Round' },
  { value: 'round_4', label: '4th Round' },
  { value: 'last_64', label: 'Last 64' },
  { value: 'last_32', label: 'Last 32' },
  { value: 'last_16', label: 'Last 16' },
  { value: 'qf', label: 'Quarter Final' },
  { value: 'sf', label: 'Semi Final' },
  { value: 'final', label: 'Final' }
];

/**
 * @param {Object} props
 * @param {Object} props.formData - Match form data
 * @param {Function} props.onFormChange - Handler to update form data fields
 * @param {string[]} props.savedOpponents - List of saved opponent names
 * @param {string[]} props.savedCompetitions - List of saved competition names
 */
export function DetailsTab({
  formData,
  onFormChange,
  savedOpponents = [],
  savedCompetitions = [],
  isNewMatch = false,
  onImportedFullMatch,
}) {
  const updateField = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  const updateFields = (updates) => {
    onFormChange({ ...formData, ...updates });
  };

  // ============ Voice capture (only for new matches) ============
  const { user } = useAuth();
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Apply a Lineup Screenshot Import result to the match form. Mirrors
  // acceptVoiceResult but consumes the richer payload from the vision
  // endpoint (metadata + score + lineup + events). Only fields the vision
  // could actually detect are written — nulls are ignored so the coach
  // doesn't lose values they already keyed in.
  const acceptImportResult = (result) => {
    const pf = result?.pre_filled_match;
    if (!pf) return;
    const updates = {};
    if (pf.opponent && pf.opponent !== 'Unknown Opponent') updates.opponent = pf.opponent;
    if (pf.date) updates.date = pf.date;
    if (pf.kick_off_time) updates.kick_off_time = pf.kick_off_time;
    if (pf.venue) updates.venue = pf.venue;
    if (pf.competition) updates.competition = pf.competition;
    if (pf.match_type) {
      updates.match_type = pf.match_type;
      updates.is_cup = pf.match_type === 'cup_knockout' || pf.match_type === 'cup_group';
    }
    if (pf.our_score != null) updates.our_score = pf.our_score;
    if (pf.opponent_score != null) updates.opponent_score = pf.opponent_score;
    updateFields(updates);

    // Hand the full parsed payload (starters / subs / events) up to the
    // parent MatchEdit page — it holds the lineup, subs bench, and events
    // arrays and knows how to apply them to the relevant tabs.
    if (onImportedFullMatch && (pf.starters?.length || pf.subs_bench?.length || pf.events?.length)) {
      onImportedFullMatch({
        starters: pf.starters || [],
        subs_bench: pf.subs_bench || [],
        events: pf.events || [],
      });
    }

    const summary = [];
    if (pf.starters?.length) summary.push(`${pf.starters.length} starters`);
    if (pf.subs_bench?.length) summary.push(`${pf.subs_bench.length} subs`);
    if (pf.events?.length) summary.push(`${pf.events.length} events`);
    const suffix = summary.length ? ` · ${summary.join(', ')} applied to lineup + events tabs` : '';
    toast.success(`Match details imported from screenshot${suffix}`);
  };

  const handleVoiceTranscript = async (text) => {
    if (!text) return;
    setVoiceParsing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/matches/parse-voice-new-match`,
        { transcript: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.error) {
        toast.error(res.data.error);
        setVoiceResult(null);
      } else {
        setVoiceResult(res.data);
      }
    } catch (err) {
      console.error('Voice parse failed:', err);
      toast.error('Failed to process voice input');
    } finally {
      setVoiceParsing(false);
    }
  };

  const voice = useVoiceCapture({
    engine: user?.voice_engine || 'webspeech',
    onResult: handleVoiceTranscript,
    onError: (msg) => toast.error(msg),
    prompt: 'Soccer match details: opponent, competition, match type, date, kick-off time, venue.',
  });

  const acceptVoiceResult = () => {
    if (!voiceResult) return;
    const updates = {};
    if (voiceResult.opponent) updates.opponent = voiceResult.opponent;
    if (voiceResult.competition) updates.competition = voiceResult.competition;
    if (voiceResult.match_type) {
      updates.match_type = voiceResult.match_type;
      updates.is_cup = voiceResult.match_type === 'cup_knockout' || voiceResult.match_type === 'cup_group';
    }
    if (voiceResult.date) updates.date = voiceResult.date;
    if (voiceResult.kick_off_time) updates.kick_off_time = voiceResult.kick_off_time;
    if (voiceResult.venue) updates.venue = voiceResult.venue;
    updateFields(updates);
    setVoiceResult(null);
    toast.success('Match details filled from voice');
  };

  const rejectVoiceResult = () => {
    setVoiceResult(null);
  };

  const showVoiceSection = isNewMatch && voice.isSupported;

  return (
    <Card className="rounded-2xl" data-testid="details-tab-content">
      <CardContent className="p-6 space-y-6">
        {/* Screenshot Import for new matches — works on all devices,
            independent of voice capture support. */}
        {isNewMatch && (
          <div className="space-y-2" data-testid="screenshot-import-new-match-section">
            <Button
              type="button"
              variant="outline"
              onClick={() => setImportModalOpen(true)}
              className="w-full h-12 rounded-xl flex items-center justify-center gap-2 border-2 border-[#007a33]/40 text-[#007a33] hover:bg-[#007a33]/5"
              data-testid="import-screenshot-btn"
            >
              <Camera className="w-5 h-5" />
              Import from screenshot
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Drop a screenshot from your league app, WhatsApp, or ref card &mdash; we&apos;ll read the details for you.
            </p>
          </div>
        )}

        <LineupImportModal
          open={importModalOpen}
          onOpenChange={setImportModalOpen}
          onImportComplete={acceptImportResult}
        />

        {/* Voice-fill for new matches */}
        {showVoiceSection && (
          <div className="space-y-3" data-testid="voice-new-match-section">
            {!voiceResult && (
              <Button
                type="button"
                {...voice.pressHandlers}
                disabled={voiceParsing || voice.isTranscribing}
                className={`w-full h-12 rounded-xl flex items-center justify-center gap-2 transition-colors select-none ${
                  voice.isListening
                    ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                    : 'bg-[#007a33] hover:bg-[#005c26] text-white'
                }`}
                data-testid="voice-new-match-btn"
              >
                {(voiceParsing || voice.isTranscribing) ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : voice.isListening ? (
                  <>
                    <Mic className="w-5 h-5" />
                    {voice.tapStopArmed ? 'Recording... tap to stop' : 'Listening...'}
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" />
                    Fill with voice
                  </>
                )}
              </Button>
            )}
            {!voiceResult && !voice.isListening && !voiceParsing && !voice.isTranscribing && (
              <p className="text-xs text-gray-500 text-center">
                Tap to record · long-press for hands-free mode. Say opponent, competition, match type, date, kick-off time, and venue.
              </p>
            )}

            {/* Confirmation card */}
            {voiceResult && (
              <div
                className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3"
                data-testid="voice-new-match-confirmation"
              >
                <div className="flex items-start gap-2">
                  <Mic className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-gray-500 flex-1 italic">&ldquo;{voiceResult.raw_transcript}&rdquo;</p>
                  {voiceResult.confidence && (
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide ${
                        voiceResult.confidence === 'high'
                          ? 'bg-emerald-100 text-emerald-700'
                          : voiceResult.confidence === 'medium'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-200 text-gray-600'
                      }`}
                      data-testid="voice-new-match-confidence"
                    >
                      {voiceResult.confidence}
                    </span>
                  )}
                </div>

                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <dt className="text-gray-500">Opponent</dt>
                  <dd className="text-gray-900 font-medium text-right">
                    {voiceResult.opponent || <span className="text-gray-400">—</span>}
                    {voiceResult.opponent && !voiceResult.opponent_matched && (
                      <span className="ml-1.5 text-[10px] text-amber-600 uppercase">new</span>
                    )}
                  </dd>
                  <dt className="text-gray-500">Competition</dt>
                  <dd className="text-gray-900 font-medium text-right">
                    {voiceResult.competition || <span className="text-gray-400">—</span>}
                    {voiceResult.competition && !voiceResult.competition_matched && (
                      <span className="ml-1.5 text-[10px] text-amber-600 uppercase">new</span>
                    )}
                  </dd>
                  <dt className="text-gray-500">Match type</dt>
                  <dd className="text-gray-900 font-medium text-right">
                    {voiceResult.match_type ? (MATCH_TYPE_LABELS[voiceResult.match_type] || voiceResult.match_type) : <span className="text-gray-400">—</span>}
                  </dd>
                  <dt className="text-gray-500">Date</dt>
                  <dd className="text-gray-900 font-medium text-right">{voiceResult.date || <span className="text-gray-400">—</span>}</dd>
                  <dt className="text-gray-500">Kick-off</dt>
                  <dd className="text-gray-900 font-medium text-right">{voiceResult.kick_off_time || <span className="text-gray-400">—</span>}</dd>
                  <dt className="text-gray-500">Venue</dt>
                  <dd className="text-gray-900 font-medium text-right capitalize">{voiceResult.venue || <span className="text-gray-400">—</span>}</dd>
                </dl>

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    onClick={acceptVoiceResult}
                    className="flex-1 bg-[#007a33] hover:bg-[#005c26] text-white rounded-lg h-9"
                    data-testid="voice-new-match-accept"
                  >
                    <Check className="w-4 h-4 mr-1.5" />
                    Use these details
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={rejectVoiceResult}
                    className="h-9 rounded-lg"
                    data-testid="voice-new-match-reject"
                  >
                    <XIcon className="w-4 h-4 mr-1.5" />
                    Try again
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Opponent *</Label>
            <ComboboxInput
              value={formData.opponent}
              onChange={(value) => updateField('opponent', value)}
              options={savedOpponents.map(o => o.name)}
              placeholder="Select or type opponent..."
              testId="match-opponent-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Competition</Label>
            <ComboboxInput
              value={formData.competition}
              onChange={(value) => updateField('competition', value)}
              options={savedCompetitions.map(c => c.name)}
              placeholder="Select or type competition..."
              testId="match-competition-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Date *</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => updateField('date', e.target.value)}
              className="rounded-xl"
              data-testid="match-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Kick-off Time</Label>
            <Input
              type="time"
              value={formData.kick_off_time}
              onChange={(e) => updateField('kick_off_time', e.target.value)}
              className="rounded-xl"
              data-testid="match-time-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Venue</Label>
            <Select value={formData.venue} onValueChange={(v) => updateField('venue', v)}>
              <SelectTrigger className="rounded-xl" data-testid="match-venue-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VENUES.map(v => (
                  <SelectItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>


        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => updateField('status', v)}>
              <SelectTrigger className="rounded-xl" data-testid="match-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Player Dues (£)</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.player_dues}
              onChange={(e) => updateField('player_dues', e.target.value)}
              placeholder="0.00"
              className="rounded-xl"
              data-testid="match-dues-input"
            />
          </div>
        </div>

        {/* Match Type */}
        <div className="space-y-2">
          <Label>Match Type</Label>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'league', label: 'League' },
              { id: 'cup_knockout', label: 'Cup (Knockout)' },
              { id: 'cup_group', label: 'Cup (Group Stage)' },
              { id: 'friendly', label: 'Friendly' },
            ].map(mt => (
              <button
                key={mt.id}
                type="button"
                onClick={() => {
                  const isCup = mt.id === 'cup_knockout' || mt.id === 'cup_group';
                  updateFields({
                    match_type: mt.id,
                    is_cup: isCup,
                    cup_round: isCup ? formData.cup_round : '',
                    our_score_et: '', opponent_score_et: '',
                    our_score_pens: '', opponent_score_pens: ''
                  });
                }}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  formData.match_type === mt.id
                    ? 'bg-emerald-600 text-white ring-1 ring-emerald-400'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`match-type-${mt.id}`}
              >
                {mt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Half Length (shown when match type selected) */}
        {formData.match_type && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Half Length (mins)</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={formData.half_length}
                onChange={(e) => updateField('half_length', e.target.value)}
                placeholder="45"
                className="rounded-xl"
                data-testid="match-half-length-input"
              />
            </div>
            {formData.match_type === 'cup_knockout' && (
              <div className="space-y-2">
                <Label>Extra Time Half (mins)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={formData.extra_time_half_length}
                  onChange={(e) => updateField('extra_time_half_length', e.target.value)}
                  placeholder="15"
                  className="rounded-xl"
                  data-testid="match-extra-time-input"
                />
              </div>
            )}
          </div>
        )}

        {/* Cup Round Selector */}
        {(formData.match_type === 'cup_knockout' || formData.match_type === 'cup_group') && (
          <div className="space-y-2">
            <Label>Cup Round</Label>
            <Select
              value={formData.cup_round}
              onValueChange={(value) => updateField('cup_round', value)}
            >
              <SelectTrigger className="rounded-xl" data-testid="match-cup-round">
                <SelectValue placeholder="Select round..." />
              </SelectTrigger>
              <SelectContent>
                {CUP_ROUNDS.filter(r => formData.match_type === 'cup_group' || r.value !== 'group_stage').map((round) => (
                  <SelectItem key={round.value} value={round.value}>
                    {round.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Cup Group Result */}
        {formData.match_type === 'cup_group' && (
          <div className="space-y-2">
            <Label>Group Stage Result</Label>
            <Select
              value={formData.cup_group_result}
              onValueChange={(value) => updateField('cup_group_result', value)}
            >
              <SelectTrigger className="rounded-xl" data-testid="match-cup-group-result">
                <SelectValue placeholder="Select result..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="qualified">Qualified</SelectItem>
                <SelectItem value="eliminated">Eliminated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Score Section (for Result status) */}
        {formData.status === 'result' && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <Label className="text-sm font-medium">Match Score</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Our Score</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.our_score}
                  onChange={(e) => updateField('our_score', e.target.value)}
                  className="rounded-xl"
                  data-testid="match-our-score-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Opponent Score</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.opponent_score}
                  onChange={(e) => updateField('opponent_score', e.target.value)}
                  className="rounded-xl"
                  data-testid="match-opponent-score-input"
                />
              </div>
            </div>

            {/* Extra Time (Cup only, when draw) */}
            {formData.is_cup && formData.match_type !== 'cup_group' && formData.our_score !== '' && formData.opponent_score !== '' && formData.our_score === formData.opponent_score && (
              <div className="space-y-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <Label className="text-sm font-medium text-amber-800">After Extra Time</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Our Score (AET)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.our_score_et}
                      onChange={(e) => updateField('our_score_et', e.target.value)}
                      className="rounded-xl"
                      data-testid="match-our-score-et-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Opponent Score (AET)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.opponent_score_et}
                      onChange={(e) => updateField('opponent_score_et', e.target.value)}
                      className="rounded-xl"
                      data-testid="match-opponent-score-et-input"
                    />
                  </div>
                </div>

                {/* Penalties (when ET is also a draw) */}
                {formData.our_score_et !== '' && formData.opponent_score_et !== '' && formData.our_score_et === formData.opponent_score_et && (
                  <div className="space-y-4 p-3 bg-red-50 rounded-xl border border-red-200 mt-3">
                    <Label className="text-sm font-medium text-red-800">Penalties</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Our Score (Pens)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.our_score_pens}
                          onChange={(e) => updateField('our_score_pens', e.target.value)}
                          className="rounded-xl"
                          data-testid="match-our-score-pens-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Opponent Score (Pens)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.opponent_score_pens}
                          onChange={(e) => updateField('opponent_score_pens', e.target.value)}
                          className="rounded-xl"
                          data-testid="match-opponent-score-pens-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
